'use client';

import { add } from 'date-fns';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { useLocale } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import { useCookie } from 'react-use';
import { buildRequestSource } from 'utils';
import { parseDate } from 'utils/DateFormats';
import JourneyDescriptionMobile from '~/components/Journey/JourneyDescriptionMobile';
import JourneyHeader from '~/components/Journey/JourneyHeader';
import JourneyIncludes from '~/components/Journey/JourneyIncludes';
import JourneyIntro from '~/components/Journey/JourneyIntro';
import JourneyMobileCTABar from '~/components/Journey/JourneyMobileCTABar';
import JourneySecureBooking from '~/components/Journey/JourneySecureBooking';
import JourneyTopBar from '~/components/Journey/JourneyTopBar';
import { MajorErrorBanner } from '~/components/Journey/MajorErrorBanner/MajorErrorBanner';
import { PriceBar } from '~/components/Journey/PriceBar/PriceBar';
import { SaveTripPrompt } from '~/components/Journey/SaveTripPrompt';
import { StickyTripHeader } from '~/components/Journey/StickyTripHeader';
import { JourneyMessagingDesktop, JourneyMessagingMobile } from '~/components/JourneyMessaging/JourneyMessaging';
import { Box } from '~/components/Layout/Box';
import { useInteractiveLoader } from '~/components/PartnerLoader/PartnerLoader';
import { TrustBox } from '~/components/Trustpilot';
import { COUNTRY_COOKIE } from '~/constants/cookies';
import { getDefaultOriginId } from '~/constants/default-origin';
import JourneyDataContext, { type FuturePricingTiers, type UnavailableDates } from '~/contexts/JourneyDataContext';
import { useScrollToTopOnLoad } from '~/hooks/useScrollToTopOnLoad';
import useSendFacebookPageViewEvent from '~/hooks/useSendFacebookPageViewEvent';
import { useRouter } from '~/i18n/navigation';
import { useJourneyActions, useJourneyEditErrors } from '~/stores/journeyStore';
import type { JourneyTransformed } from '~/types/Journey.types';
import { isTripV2Enabled } from '~/utils/tripV2Flag';
import { appendParamsToUrl } from '~/utils/urlHelpers';
import { JourneyBpiLinkBanner } from '../../JourneyBpiLinkBanner/JourneyBpiLinkBanner';
import { JourneyTopBarLoader } from '../../loaders/JourneyTopBarLoader';

const JourneyItinerary = dynamic(() => import('~/components/Journey/JourneyItinerary'), {
  ssr: false,
  loading: () => null
});

const JourneyMapForTrip = dynamic(
  () => import('~/components/Journey/JourneyMap').then((module) => module.JourneyMapForTrip),
  { ssr: false, loading: () => null }
);

interface TemplateTripProps {
  templateTrip: JourneyTransformed; // TODO: Type this properly
  user: any; // TODO: Type this properly
  loyaltyDiscount?: number;
  loyaltyTierId?: string;
  loyaltyTierName?: string;
}

export default function TemplateTrip({
  templateTrip,
  user,
  loyaltyDiscount = 0,
  loyaltyTierId,
  loyaltyTierName
}: Readonly<TemplateTripProps>) {
  const router = useRouter();
  const defaultOriginId = getDefaultOriginId(useLocale());
  useScrollToTopOnLoad();
  const searchParams = useSearchParams();
  const journeyTitleOverride = searchParams?.get('title');

  const journeyEditErrors = useJourneyEditErrors();
  const { setJourneyEditErrors } = useJourneyActions();

  const { completeProgressBar } = useInteractiveLoader();

  const [countryCodeOfResidence] = useCookie(COUNTRY_COOKIE);

  useSendFacebookPageViewEvent('ViewContent');

  const { details, featuredActivities, region, source, unavailableStartDates, uniqueId, overrideEditableTrip } =
    templateTrip;

  const { cheapestAvailableDate, futureDatesPriceTiered, tripInDays, cheapestAvailableDateUpdated } = details;

  const futurePricingTiers: FuturePricingTiers = useMemo(
    () =>
      cheapestAvailableDate && futureDatesPriceTiered
        ? {
            blueprintId: uniqueId,
            cheapestAvailableDate:
              countryCodeOfResidence === 'US'
                ? cheapestAvailableDate.startDateUS || cheapestAvailableDate.startDate
                : cheapestAvailableDate.startDate,
            pricingTiers: futureDatesPriceTiered,
            cheapestAvailableDateUpdated: cheapestAvailableDateUpdated
          }
        : null,
    [cheapestAvailableDate, cheapestAvailableDateUpdated, countryCodeOfResidence, futureDatesPriceTiered, uniqueId]
  );

  const unavailableDates: UnavailableDates = useMemo(
    () => ({
      blueprintId: uniqueId,
      dates: unavailableStartDates ?? null
    }),
    [uniqueId, unavailableStartDates]
  );

  // FEATURE FLAG
  const isTripV2 = isTripV2Enabled();
  let removeBlueprintTemplateStep = isTripV2 || process.env.NEXT_PUBLIC_REMOVE_BLUEPRINT_TEMPLATE_STEP === 'true';

  if (!futureDatesPriceTiered && !isTripV2) removeBlueprintTemplateStep = false;

  const isEditable =
    !removeBlueprintTemplateStep && (typeof overrideEditableTrip === 'boolean' ? overrideEditableTrip : true);

  const [showLoader, setShowLoader] = useState(removeBlueprintTemplateStep);
  const [url, setUrl] = useState('');

  useEffect(() => {
    const abortController = new AbortController();
    let isActive = true;

    if (unavailableStartDates?.length) {
      sessionStorage.setItem('unavailableDates', JSON.stringify(unavailableDates));
    }

    if (removeBlueprintTemplateStep) {
      if (isTripV2 && (!cheapestAvailableDate?.startDate || !futureDatesPriceTiered)) {
        const keepsDefaultDates =
          source.creationSource === 'FROM_VIRTUAL_BLUEPRINT_TEMPLATE' ||
          source.creationSource === 'FROM_VIRTUAL_BLUEPRINT' ||
          source.creationSource.includes('PLAYGROUND');
        const tripUrl = new URL(
          appendParamsToUrl(`/journey/${uniqueId}`, Object.fromEntries(searchParams?.entries() ?? [])),
          globalThis.location.origin
        );
        for (const key of ['title', 'lp']) {
          const value = searchParams?.get(key);
          if (value) tripUrl.searchParams.set(key, value);
        }
        if (!keepsDefaultDates) tripUrl.searchParams.set('template', '1');
        router.replace(`${tripUrl.pathname}${tripUrl.search}`);
        return;
      }

      if (!cheapestAvailableDate?.startDate) {
        setJourneyEditErrors(['UNKNOWN_FAILURE']);
        completeProgressBar();
        sessionStorage.removeItem('futurePricingTiers');
        setShowLoader(false);
        return;
      }

      sessionStorage.setItem('futurePricingTiers', JSON.stringify(futurePricingTiers));

      const featuredActivityIds = featuredActivities.map((activity) => activity.id);
      const startDateAsDate = parseDate(
        countryCodeOfResidence === 'US'
          ? cheapestAvailableDate.startDateUS || cheapestAvailableDate.startDate
          : cheapestAvailableDate.startDate
      );
      const endDate = add(startDateAsDate, { days: tripInDays - 1 });

      // set title override
      if (journeyTitleOverride) user.journeyTitleOverride = journeyTitleOverride;

      const getBlueprintJourney = async () => {
        try {
          const res = await fetch('/api/template-journey', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              adult_count: user.numberOfAdults,
              childAges: [], //DEFAULT
              room_count: 1, //DEFAULT
              closest_city: defaultOriginId, //DEFAULT
              duration: tripInDays,
              startDate:
                countryCodeOfResidence === 'US'
                  ? cheapestAvailableDate.startDateUS || cheapestAvailableDate.startDate
                  : cheapestAvailableDate.startDate,
              endDate: endDate,
              twinRoomPreferred: false, //DEFAULT
              ...(featuredActivities && {
                featuredActivities: featuredActivityIds
              }),
              journeyId: uniqueId,
              originLocationId: defaultOriginId, //DEFAULT
              region: region.title,
              buildParameters: user,
              requestSource: buildRequestSource({
                environment: process.env.NEXT_PUBLIC_SOURCE_ENV,
                source: searchParams?.get('source'),
                lp: searchParams?.get('lp'),
                action: 'create'
              })
            }),
            signal: abortController.signal
          });

          completeProgressBar();

          if (!isActive) return;

          if (!res.ok) {
            setJourneyEditErrors(['UNKNOWN_FAILURE']);
            return;
          }

          const data = await res.json();
          const { failureMessages, uniqueId: tripId } = data.data;

          if (failureMessages.length > 0) {
            setJourneyEditErrors(['UNKNOWN_FAILURE']);
          } else {
            setJourneyEditErrors([]);
            setUrl(appendParamsToUrl(`/journey/${tripId}`, Object.fromEntries(searchParams?.entries() ?? [])));
          }
        } catch (err) {
          if (err instanceof Error && err.name === 'AbortError') {
            return;
          }
          if (isActive) {
            completeProgressBar();
            setJourneyEditErrors(['UNKNOWN_FAILURE']);
            setShowLoader(false);
          }
        }
      };
      getBlueprintJourney();
    }
    if (!removeBlueprintTemplateStep) sessionStorage.removeItem('futurePricingTiers');
    setShowLoader(false);

    return () => {
      isActive = false;
      abortController.abort();
    };
  }, [removeBlueprintTemplateStep, cheapestAvailableDate, unavailableStartDates]);

  useEffect(() => {
    // if no errors creating Journey from Blueprint, and url is set
    // then redirect to journey page with new journey set on the earliest available cheapest date
    if (removeBlueprintTemplateStep && !showLoader && !journeyEditErrors.length && url) {
      router.replace(url);
    }
  }, [removeBlueprintTemplateStep, showLoader, journeyEditErrors, url]);

  const providerValue = useMemo(
    () => ({
      user,
      journeyType: 'asTemplate',
      urlPath: 'journey-template',
      unavailableDates,
      loyaltyDiscount,
      loyaltyTierId,
      loyaltyTierName
    }),
    [loyaltyDiscount, loyaltyTierId, loyaltyTierName, unavailableDates, user]
  );

  return (
    <JourneyDataContext.Provider value={providerValue}>
      {!showLoader && journeyEditErrors.length ? (
        <>
          <div className="sticky top-0 z-[999] bg-white pb-4">
            <div className="bg-blue-100">
              <div className="container mx-auto md:px-8">
                <JourneyTopBar isTemplateTrip={true} isEditable={isEditable} />
              </div>
            </div>
            <div className="container mx-auto md:px-8">
              <PriceBar />
            </div>
          </div>
          <StickyTripHeader />
          <SaveTripPrompt />
          <div className="container mx-auto md:px-8">
            <JourneyMessagingDesktop />
            <JourneyIncludes />
            <MajorErrorBanner />
            <JourneyHeader />
            <JourneyMobileCTABar isTemplateTrip={true} isEditable={isEditable} />
            <JourneyDescriptionMobile />
            <JourneyMessagingMobile />
            <JourneyIncludes variant="journey-mobile" />
            <hr className="mx-4 my-8 md:hidden border-neutral-200" />
            <JourneyIntro />
            <JourneyMapForTrip />
            <hr className="mx-4 mt-8 md:mx-0 md:mt-16" />
            <JourneyItinerary />
            <JourneyBpiLinkBanner />
            <div className="mb-24 md:space-y-8 md:mb-8">
              <JourneySecureBooking />
              <Box space="--by-space-800">
                <TrustBox templateId="539ad0ffdec7e10e686debd7" />
              </Box>
            </div>
          </div>
        </>
      ) : removeBlueprintTemplateStep ? (
        <JourneyTopBarLoader />
      ) : (
        <>
          <div className="sticky top-0 z-[999] bg-white pb-4">
            <div className="bg-blue-100">
              <div className="container mx-auto md:px-8">
                <JourneyTopBar isTemplateTrip={true} isEditable={isEditable} />
              </div>
            </div>
            <div className="container mx-auto md:px-8">
              <PriceBar />
            </div>
          </div>
          <StickyTripHeader />
          <SaveTripPrompt />
          <div className="container mx-auto md:px-8">
            <JourneyMessagingDesktop />
            <JourneyIncludes />
            <JourneyHeader />
            <JourneyMobileCTABar isTemplateTrip={true} isEditable={isEditable} />
            <JourneyDescriptionMobile />
            <JourneyMessagingMobile />
            <JourneyIncludes variant="journey-mobile" />
            <hr className="mx-4 my-8 md:hidden border-neutral-200" />
            <JourneyIntro />
            <JourneyMapForTrip />
            <hr className="mx-4 mt-8 md:mx-0 md:mt-16" />
            <JourneyItinerary />
            <JourneyBpiLinkBanner />
            <div className="mb-8 space-y-8">
              <JourneySecureBooking />
              <Box space="--by-space-800">
                <TrustBox templateId="539ad0ffdec7e10e686debd7" />
              </Box>
            </div>
          </div>
        </>
      )}
    </JourneyDataContext.Provider>
  );
}
